import {useState} from 'react';
import {Link} from 'react-router-dom';
import {ArrowRight, ExternalLink, History, Loader2} from 'lucide-react';
import {Alert, AlertDescription} from '@common-web/components/ui/alert';
import {useDataTable} from '@common-web/components/data-table';
import {ListPaginationFooter} from '@common-web/components/list-pagination-footer';
import {trpc, RouterOutput} from '@common-web/lib/trpc';
import {formatAuditAction} from '@internal/pages/audit/auditFormat';
import {AuditEntrySheet} from '@internal/pages/audit/AuditEntrySheet';

type AuditEntry = RouterOutput['internal']['auditLog']['listForEntity']['entries'][number];

type EntityHistoryTabProps = {
  entityType:
    | 'GROUP'
    | 'MEMBER'
    | 'ACCOUNT'
    | 'INVOICE'
    | 'CARRIER_INVOICE'
    | 'PAYOUT_PERIOD'
    | 'AGENT'
    | 'USER'
    | 'GROUP_PLAN';
  entityId: string;
  /** Match `entityId` as a prefix (e.g. members keyed `<subscriberId>-<seq>`). */
  matchPrefix?: boolean;
};

function relativeTime(iso: string | Date): string {
  const diff = Date.now() - new Date(iso).getTime();
  const minutes = Math.floor(diff / 60_000);
  if (minutes < 1) return 'just now';
  if (minutes < 60) return `${minutes}m ago`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.floor(hours / 24);
  if (days < 30) return `${days}d ago`;
  return new Date(iso).toLocaleDateString();
}

function absoluteTime(iso: string | Date): string {
  return new Date(iso).toLocaleString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  });
}

/** Dot color by the kind of action, so terminations / creations read at a glance. */
function dotColorClass(action: string): string {
  if (/TERMINAT|REMOV|DELET|VOID|REJECT|DISPUT|ARCHIV/i.test(action)) return 'bg-red-600';
  if (/CREAT|ADD|ENROLL|REINSTAT|REACTIVAT|GENERAT|PAID|RESOLV|ACKNOWLEDG/i.test(action)) {
    return 'bg-green-600';
  }
  if (/STATUS|HOLD|UPDAT|CHANG|REASSIGN|COPI|APPL/i.test(action)) return 'bg-primary';
  return 'bg-muted-foreground';
}

/**
 * Reusable per-entity change history backed by the unified `AuditLog`. Mounted
 * as a History tab on detail pages (internal portal only). Renders a
 * newest-first vertical timeline of who-did-what-when with before/after value
 * chips; clicking an entry opens the full audit detail sheet.
 */
export function EntityHistoryTab({
  entityType,
  entityId,
  matchPrefix = false,
}: EntityHistoryTabProps) {
  const [selectedEntry, setSelectedEntry] = useState<AuditEntry | null>(null);
  const table = useDataTable({defaultPageSize: 20, syncToUrl: 'history'});

  const {data, isLoading, error} = trpc.internal.auditLog.listForEntity.useQuery(
    {entityType, entityId, matchPrefix, page: table.page, pageSize: table.pageSize},
    {enabled: !!entityId},
  );

  const entries = data?.entries ?? [];
  const totalCount = data?.totalCount ?? 0;
  const totalPages = data?.totalPages ?? 1;

  const auditLogHref = `/audit/activity?entityType=${entityType}&search=${encodeURIComponent(entityId)}`;

  return (
    <>
      <div className='rounded-xl border bg-card p-5'>
        <div className='mb-4 flex items-center justify-between gap-3'>
          <div className='flex items-center gap-2'>
            <History className='h-4 w-4 text-muted-foreground' />
            <h3 className='text-[14px] font-semibold text-foreground'>History</h3>
            {totalCount > 0 && (
              <span className='rounded-full bg-primary/10 px-2 py-0.5 text-xs font-semibold text-primary'>
                {totalCount}
              </span>
            )}
          </div>
          <Link
            to={auditLogHref}
            className='inline-flex items-center gap-1 text-[12px] font-semibold text-primary hover:underline'
          >
            View in Audit Log <ExternalLink className='h-3 w-3' />
          </Link>
        </div>

        {isLoading ? (
          <div className='flex items-center justify-center gap-2 py-8 text-[13px] text-muted-foreground'>
            <Loader2 className='h-4 w-4 animate-spin' />
            Loading history…
          </div>
        ) : error ? (
          <Alert variant='destructive' className='border-red-200 bg-red-50'>
            <AlertDescription>{error.message}</AlertDescription>
          </Alert>
        ) : entries.length === 0 ? (
          <p className='py-8 text-center text-[13px] text-muted-foreground'>
            No recorded activity for this record yet.
          </p>
        ) : (
          <ol className='relative'>
            {entries.map((e, i) => {
              const isLast = i === entries.length - 1;
              return (
                <li key={e.id} className='relative pb-5 pl-6 last:pb-0'>
                  {!isLast && (
                    <span className='absolute bottom-0 left-[5px] top-3 w-px bg-border' />
                  )}
                  <span
                    className={`absolute left-0 top-1.5 h-[11px] w-[11px] rounded-full border-2 border-card ${dotColorClass(e.action)}`}
                  />
                  <button
                    type='button'
                    onClick={() => setSelectedEntry(e)}
                    className='block w-full rounded-md text-left transition-colors hover:bg-muted/40'
                  >
                    <div className='flex items-baseline justify-between gap-3'>
                      <span className='text-[13px] font-semibold text-foreground'>
                        {formatAuditAction(e.action)}
                      </span>
                      <span
                        className='shrink-0 whitespace-nowrap text-xs text-muted-foreground'
                        title={absoluteTime(e.timestamp)}
                      >
                        {relativeTime(e.timestamp)}
                      </span>
                    </div>
                    <div className='mt-0.5 text-[12px] text-muted-foreground'>
                      by {e.changedBy}
                      {e.fieldChanged ? (
                        <>
                          {' '}
                          · <span className='font-medium'>{e.fieldChanged}</span>
                        </>
                      ) : null}
                    </div>
                    {(e.oldValue || e.newValue) && (
                      <div className='mt-1.5 flex flex-wrap items-center gap-1.5 text-[12px]'>
                        {e.oldValue && (
                          <span className='rounded bg-red-50 px-1.5 py-0.5 text-red-800 line-through'>
                            {e.oldValue}
                          </span>
                        )}
                        <ArrowRight className='h-3 w-3 text-muted-foreground' />
                        {e.newValue && (
                          <span className='rounded bg-green-50 px-1.5 py-0.5 text-green-800'>
                            {e.newValue}
                          </span>
                        )}
                      </div>
                    )}
                    {e.noteText && (
                      <p className='mt-1 rounded bg-muted/50 p-2 text-[12px] italic text-muted-foreground'>
                        “{e.noteText}”
                      </p>
                    )}
                  </button>
                </li>
              );
            })}
          </ol>
        )}

        {totalPages > 1 && (
          <div className='mt-4'>
            <ListPaginationFooter
              page={table.page}
              pageSize={table.pageSize}
              total={totalCount}
              totalPages={totalPages}
              onPageChange={table.setPage}
              onPageSizeChange={table.setPageSize}
              rangeEntityLabel='changes'
            />
          </div>
        )}
      </div>
      {selectedEntry && (
        <AuditEntrySheet entry={selectedEntry} onClose={() => setSelectedEntry(null)} />
      )}
    </>
  );
}
